import React, { useState, useEffect } from 'react';
import { Lang, t } from '../../../i18n';
import { TextToSpeechService } from '../../../services/textToSpeech';
import { GlobeIcon, ThemeIcon, SpeakerIcon, LogoutIcon } from './utils/Icons';
import './TopBar.css';

interface TopBarProps {
  lang?: Lang;
  setLang?: (lang: Lang) => void;
  textToSpeechEnabled?: boolean;
  setTextToSpeechEnabled?: (enabled: boolean) => void;
  onLogout: () => void;
  showLanguageToggle?: boolean;
  showTtsToggle?: boolean;
  showThemeToggle?: boolean;
}

const TopBar: React.FC<TopBarProps> = ({
  lang = 'nl',
  setLang,
  textToSpeechEnabled = false,
  setTextToSpeechEnabled,
  onLogout,
  showLanguageToggle = true,
  showTtsToggle = true,
  showThemeToggle = true,
}) => {
  // Light theme is the default; dark can be toggled on.
  const [dark, setDark] = useState(false);

  useEffect(() => {
    if (!showThemeToggle) {
      return;
    }
    if (dark) {
      document.body.classList.add('dark');
      document.body.classList.remove('light');
    } else {
      document.body.classList.remove('dark');
      document.body.classList.add('light');
    }
  }, [dark, showThemeToggle]);

  useEffect(() => {
    if (!showTtsToggle || !setTextToSpeechEnabled) {
      return;
    }

    if (lang === 'en' && textToSpeechEnabled) {
      setTextToSpeechEnabled(false);
      TextToSpeechService.textToSpeechEnabled = false;
      console.log('Text-to-speech automatically disabled - switching to English');
    }
  }, [lang, textToSpeechEnabled, setTextToSpeechEnabled, showTtsToggle]);

  const handleLanguageToggle = () => {
    if (!setLang) {
      return;
    }
    const newLang = lang === 'en' ? 'nl' : 'en';
    setLang(newLang);
    TextToSpeechService.currentLanguage = newLang;
  };

  const handleTtsToggle = () => {
    if (!setTextToSpeechEnabled || lang === 'en') {
      return;
    }
    const newState = !textToSpeechEnabled;
    setTextToSpeechEnabled(newState);
    TextToSpeechService.textToSpeechEnabled = newState;
    console.log(`Text-to-speech ${newState ? 'enabled' : 'disabled'}`);
  };

  return (
    <div className="chat-topbar" role="banner">
      <div className="chat-topbar-controls">
        {showLanguageToggle && setLang && (
          <button
            className="lang-btn"
            title={t(lang, 'changeLanguage')}
            onClick={handleLanguageToggle}
            type="button"
          >
            <GlobeIcon />
            <span className="lang-label">{lang === 'en' ? 'EN' : 'NL'}</span>
          </button>
        )}

        {showTtsToggle && setTextToSpeechEnabled && (
          <button
            className="tts-btn disabled"
            title="Coming soon"
            // Temporarily disable text-to-speech toggle functionality
            onClick={() => {}}
            disabled={true}
            type="button"
          >
            <SpeakerIcon />
          </button>
        )}

        {showThemeToggle && (
          <button
            className="theme-btn"
            title={t(lang, 'toggleTheme')}
            onClick={() => setDark((prev) => !prev)}
            type="button"
          >
            <ThemeIcon dark={dark} />
          </button>
        )}

        <button
          type="button"
          className="topbar-logout"
          onClick={onLogout}
          aria-label={t(lang, 'logout')}
        >
          <LogoutIcon />
        </button>
      </div>
    </div>
  );
};

export default TopBar;

